import { apiFetch } from './auth-service';

export type PitchStatus = 'none' | 'draft' | 'submitted' | 'approved' | 'published';
export type IdeaStatus = 'none' | 'draft' | 'submitted' | 'approved' | 'revision_needed';

export interface FounderRow {
  startupId: string;
  startupName: string;
  startupTagline: string;
  founderId: string;
  founderName: string;
  founderRegion: string;
  industry: string;
  stage: 'idea' | 'mvp' | 'growth' | 'scale';
  currentPhase: number;
  currentPhaseTitle: string;
  phaseProgressPct: number;
  courseworkPct: number;
  sessionsAttended: number;
  sessionsInvited: number;
  pitchStatus: PitchStatus;
  ideaStatus: IdeaStatus;
}

export interface UpcomingSession {
  id: string;
  title: string;
  scheduledAt: string;
  mentorName: string;
  status: string;
}

export interface ActivityEvent {
  id: string;
  type: 'phase_started' | 'phase_completed' | 'session_attended' | 'idea_submitted' | 'pitch_approved' | 'pitch_published';
  at: string;
  title: string;
  subtitle?: string;
}

export interface FounderDetail extends FounderRow {
  description: string;
  problemStatement: string;
  solution: string;
  targetMarket: string;
  revenueModel: string;
  traction: string;
  teamSize: number;
  foundingYear: number;
  website: string;
  updatedAt: string;
  ideaTitle?: string;
  ideaSubmittedAt?: string | null;
  pitchAskAmount?: string;
  pitchPublishedFor: boolean;
  documents: { id: string; title: string; fileName: string; fileSize: number; uploadedAt: string }[];
  upcomingSessions: UpcomingSession[];
  activity: ActivityEvent[];
  phaseStatuses: Array<{ id: string; order: number; title: string; status: 'locked' | 'active' | 'completed'; progress: number }>;
}

export const getFounderRows = async (): Promise<FounderRow[]> => {
  const r = await apiFetch.get<{ startups: any[] }>('/startups');
  return (r.startups || []).map(s => ({
    startupId: s._id,
    startupName: s.name,
    startupTagline: s.tagline || '',
    founderId: s.founderId,
    founderName: s.founderName,
    founderRegion: s.founderRegion,
    industry: s.industry,
    stage: s.stage,
    currentPhase: s.currentPhase,
    currentPhaseTitle: s.currentPhaseTitle,
    phaseProgressPct: s.phaseProgressPct,
    courseworkPct: s.courseworkPct,
    sessionsAttended: s.sessionsAttended,
    sessionsInvited: s.sessionsInvited,
    pitchStatus: s.pitchStatus || 'none',
    ideaStatus: s.ideaStatus || 'none',
  }));
};

export const getFounderDetail = async (startupId: string): Promise<FounderDetail | null> => {
  const r = await apiFetch.get<{ startup: any }>(`/startups/${startupId}`);
  const s = r.startup;
  if (!s) return null;

  const completedPhases = (s.phaseStatuses || []).filter((p: any) => p.status === 'completed').length;
  const activePhase = (s.phaseStatuses || []).find((p: any) => p.status === 'active');
  const currentPhase = activePhase?.order || completedPhases || 1;
  const currentPhaseTitle = activePhase?.title || s.phaseStatuses?.find((p: any) => p.status === 'completed')?.title || '';
  const phaseProgressPct = activePhase ? 50 : completedPhases > 0 ? 100 : 0;
  const sessionsAttended = (s.activity || []).filter((a: any) => a.type === 'session_attended').length;

  return {
    startupId: s._id,
    startupName: s.name,
    startupTagline: s.tagline || '',
    founderId: s.founderId,
    founderName: s.founderName || '',
    founderRegion: s.founderRegion || '',
    industry: s.industry || '',
    stage: s.stage || 'idea',
    currentPhase,
    currentPhaseTitle,
    phaseProgressPct,
    courseworkPct: 0,
    sessionsAttended,
    sessionsInvited: 0,
    pitchStatus: 'none',
    ideaStatus: 'none',
    description: s.description || '',
    problemStatement: s.problemStatement || '',
    solution: s.solution || '',
    targetMarket: s.targetMarket || '',
    revenueModel: s.revenueModel || '',
    traction: s.traction || '',
    teamSize: s.teamSize || 0,
    foundingYear: s.foundingYear || 0,
    website: s.website || '',
    updatedAt: s.updatedAt || '',
    ideaTitle: '',
    ideaSubmittedAt: null,
    pitchAskAmount: '',
    pitchPublishedFor: false,
    documents: (s.documents || []).map((d: any) => ({
      id: d._id,
      title: d.title,
      fileName: d.fileName || d.title,
      fileSize: d.fileSize || 0,
      uploadedAt: d.uploadedAt || d.createdAt || '',
    })),
    upcomingSessions: (s.upcomingSessions || []).map((u: any) => ({
      id: u.id,
      title: u.title,
      scheduledAt: u.scheduledAt,
      mentorName: '',
      status: 'scheduled',
    })),
    activity: (s.activity || []).map((a: any, i: number) => ({
      id: `a-${i}`,
      type: a.type,
      at: a.at,
      title: a.title,
    })),
    phaseStatuses: (s.phaseStatuses || []).map((p: any) => ({
      id: p.id,
      order: p.order,
      title: p.title,
      status: p.status,
      progress: p.status === 'completed' ? 100 : p.status === 'active' ? 50 : 0,
    })),
  };
};

export const getFounderKpis = async (): Promise<{
  total: number;
  inProgram: number;
  pitchesApproved: number;
  investorReady: number;
}> => {
  const rows = await getFounderRows();
  return {
    total: rows.length,
    inProgram: rows.filter(r => r.currentPhase > 1).length,
    pitchesApproved: rows.filter(r => r.pitchStatus === 'approved' || r.pitchStatus === 'published').length,
    investorReady: rows.filter(r => r.pitchStatus === 'published').length,
  };
};
